// src/pages/PrivacyPolicy.jsx

import React, { useEffect } from 'react';
import { Shield, Lock, Eye, Database, UserCheck, Mail } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Footer from '../components/Footer';
import Header from '../components/Header';

const PrivacyPolicy = () => {
  const { language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const content = {
    en: {
      title: "Privacy Policy",
      subtitle: "Your privacy is important to us. Learn how we protect your information",
      lastUpdated: "Last Updated: December 13, 2024",
      sections: [
        {
          icon: Shield,
          title: "1. Introduction",
          content: [
            "Shree Enterprises E-Seva Kendra is committed to protecting your personal information and privacy rights.",
            "This policy explains how we collect, use, and safeguard your information when you use our services.",
            "By using our services, you agree to the collection and use of information in accordance with this policy."
          ]
        },
        {
          icon: Database,
          title: "2. Information We Collect",
          content: [
            "Personal Information: Name, date of birth, contact details, address, and photographs.",
            "Government Documents: Aadhaar, PAN card, voter ID, passport, and other identification documents.",
            "Biometric Data: Fingerprints and photos when required for government services.",
            "Payment Information: Transaction details and payment records.",
            "Technical Data: Device information and usage data when you access our website."
          ]
        },
        {
          icon: Eye,
          title: "3. How We Use Your Information",
          content: [
            "To process your applications for government documents and services.",
            "To communicate updates and important information about your service requests.",
            "To comply with legal obligations and government regulations.",
            "To maintain records as required by law for audit and compliance purposes.",
            "To improve our service quality based on feedback and usage patterns."
          ]
        },
        {
          icon: Lock,
          title: "4. Data Security & Sharing",
          content: [
            "We implement strong security measures including encryption, secure storage, and restricted access to protect your data.",
            "Your information is shared with government departments only as required to process your service requests.",
            "We may share limited data with trusted service providers (payment processors, courier services) who help us operate our business.",
            "We do not sell, rent, or trade your personal information to third parties.",
            "Physical documents are securely stored and destroyed after the legal retention period."
          ]
        },
        {
          icon: UserCheck,
          title: "5. Your Privacy Rights",
          content: [
            "You have the right to access, correct, or request deletion of your personal information.",
            "You can request copies of the data we hold about you.",
            "You may withdraw consent for data processing, though this may affect service delivery.",
            "We retain your data only as long as necessary for service delivery and legal compliance (minimum 7 years for service records).",
            "To exercise these rights, contact us using the information below."
          ]
        },
        {
          icon: Mail,
          title: "6. Contact Us",
          content: [
            "If you have questions about this Privacy Policy or our data practices, please contact us:",
            "Email: admin@shreeservz.com",
            "Phone: +91 88504 15838",
            "Address: Thane, Maharashtra, India",
            "We will respond to your inquiries within 7-10 business days."
          ]
        }
      ]
    },
    mr: {
      title: "गोपनीयता धोरण",
      subtitle: "तुमची गोपनीयता आमच्यासाठी महत्त्वाची आहे. आम्ही तुमची माहिती कशी संरक्षित करतो ते जाणून घ्या",
      lastUpdated: "शेवटचे अपडेट: डिसेंबर 13, 2024",
      sections: [
        {
          icon: Shield,
          title: "1. परिचय",
          content: [
            "श्री एंटरप्रायजेस ई-सेवा केंद्र तुमची वैयक्तिक माहिती आणि गोपनीयता अधिकारांचे संरक्षण करण्यास वचनबद्ध आहे.",
            "हे धोरण स्पष्ट करते की तुम्ही आमच्या सेवा वापरता तेव्हा आम्ही तुमची माहिती कशी गोळा, वापर आणि संरक्षित करतो.",
            "आमच्या सेवा वापरून, तुम्ही या धोरणानुसार माहिती गोळा आणि वापरण्यास सहमती देता."
          ]
        },
        {
          icon: Database,
          title: "2. आम्ही गोळा केलेली माहिती",
          content: [
            "वैयक्तिक माहिती: नाव, जन्मतारीख, संपर्क तपशील, पत्ता आणि छायाचित्रे.",
            "सरकारी दस्तऐवज: आधार, पॅन कार्ड, मतदार ओळखपत्र, पासपोर्ट आणि इतर ओळख दस्तऐवज.",
            "बायोमेट्रिक डेटा: सरकारी सेवांसाठी आवश्यक असताना बोटांचे ठसे आणि फोटो.",
            "पेमेंट माहिती: व्यवहार तपशील आणि पेमेंट रेकॉर्ड.",
            "तांत्रिक डेटा: जेव्हा तुम्ही आमची वेबसाइट वापरता तेव्हा डिव्हाइस माहिती आणि वापर डेटा."
          ]
        },
        {
          icon: Eye,
          title: "3. आम्ही तुमची माहिती कशी वापरतो",
          content: [
            "सरकारी दस्तऐवज आणि सेवांसाठी तुमचे अर्ज प्रक्रिया करण्यासाठी.",
            "तुमच्या सेवा विनंत्यांबद्दल अद्यतने आणि महत्त्वाची माहिती संप्रेषण करण्यासाठी.",
            "कायदेशीर दायित्वे आणि सरकारी नियमांचे पालन करण्यासाठी.",
            "ऑडिट आणि अनुपालन उद्देशांसाठी कायद्यानुसार आवश्यक रेकॉर्ड राखण्यासाठी.",
            "अभिप्राय आणि वापर पद्धतींवर आधारित आमची सेवा गुणवत्ता सुधारण्यासाठी."
          ]
        },
        {
          icon: Lock,
          title: "4. डेटा सुरक्षा आणि सामायिकरण",
          content: [
            "तुमचा डेटा संरक्षित करण्यासाठी आम्ही एन्क्रिप्शन, सुरक्षित स्टोरेज आणि प्रतिबंधित प्रवेशासह मजबूत सुरक्षा उपाय लागू करतो.",
            "तुमच्या सेवा विनंत्या प्रक्रिया करण्यासाठी आवश्यक असेल तेव्हाच तुमची माहिती सरकारी विभागांसह सामायिक केली जाते.",
            "आम्ही आमचा व्यवसाय चालवण्यात मदत करणाऱ्या विश्वासू सेवा प्रदात्यांसह (पेमेंट प्रोसेसर, कुरिअर सेवा) मर्यादित डेटा सामायिक करू शकतो.",
            "आम्ही तुमची वैयक्तिक माहिती तृतीय पक्षांना विकत नाही, भाड्याने देत नाही किंवा व्यापार करत नाही.",
            "भौतिक दस्तऐवज सुरक्षितपणे साठवले जातात आणि कायदेशीर धारण कालावधीनंतर नष्ट केले जातात."
          ]
        },
        {
          icon: UserCheck,
          title: "5. तुमचे गोपनीयता अधिकार",
          content: [
            "तुम्हाला तुमची वैयक्तिक माहिती ॲक्सेस, सुधारण्याचा किंवा हटवण्याची विनंती करण्याचा अधिकार आहे.",
            "आम्ही ठेवलेल्या डेटाच्या प्रती तुम्ही विनंती करू शकता.",
            "तुम्ही डेटा प्रक्रियेसाठी संमती मागे घेऊ शकता, जरी यामुळे सेवा वितरणावर परिणाम होऊ शकतो.",
            "आम्ही तुमचा डेटा केवळ सेवा वितरण आणि कायदेशीर अनुपालनासाठी आवश्यक तेवढ्या काळासाठी ठेवतो (सेवा रेकॉर्डसाठी किमान 7 वर्षे).",
            "हे अधिकार वापरण्यासाठी, खालील माहिती वापरून आमच्याशी संपर्क साधा."
          ]
        },
        {
          icon: Mail,
          title: "6. आमच्याशी संपर्क साधा",
          content: [
            "या गोपनीयता धोरण किंवा आमच्या डेटा पद्धतींबद्दल प्रश्न असल्यास, कृपया आमच्याशी संपर्क साधा:",
            "ईमेल: admin@shreeservz.com",
            "फोन: +91 88504 15838",
            "पत्ता: ठाणे, महाराष्ट्र, भारत",
            "आम्ही 7-10 कामकाजाच्या दिवसात तुमच्या चौकशींना प्रतिसाद देऊ."
          ]
        }
      ]
    }
  };

  const t = content[language];

  return (
    <>
      <style>{`
        .privacy-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #f8f9fa 0%, #fff5f2 100%);
        }

            @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        .privacy-hero {
         background: linear-gradient(-45deg, rgb(201, 75, 75), rgb(75, 19, 79), rgb(30, 58, 138), rgb(6, 95, 70)) 0% 0% / 400% 400%;
        animation: 15s ease 0s infinite normal none running gradientShift;
          color: white;
          padding: 80px 20px 60px;
          text-align: center;
          position: relative;
          overflow: hidden;
        }
          

        .privacy-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -10%;
          width: 500px;
          height: 500px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 50%;
        }

        .privacy-hero::after {
          content: '';
          position: absolute;
          bottom: -30%;
          left: -5%;
          width: 400px;
          height: 400px;
          background: rgba(255, 255, 255, 0.08);
          border-radius: 50%;
        }

        .privacy-hero-content {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .privacy-hero h1 {
          font-size: 48px;
          font-weight: 700;
          margin-bottom: 16px;
        }

        .privacy-hero p {
          font-size: 18px;
          opacity: 0.95;
          margin-bottom: 12px;
        }

        .last-updated {
          font-size: 14px;
          opacity: 0.9;
          font-weight: 500;
        }

        .privacy-container {
          max-width: 1000px;
          margin: 0 auto;
          padding: 60px 20px;
        }

        .privacy-section {
          background: white;
          border-radius: 16px;
          padding: 40px;
          margin-bottom: 24px;
          box-shadow: 0 4px 20px rgba(0,0,0,0.08);
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }

        .privacy-section:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }

        .section-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        .section-header h2 {
          font-size: 24px;
          color: #1a1a1a;
          font-weight: 700;
          margin: 0;
        }

        .section-content p {
          font-size: 16px;
          line-height: 1.8;
          color: #4a5568;
          margin-bottom: 16px;
        }

        .section-content p:last-child {
          margin-bottom: 0;
        }

        .important-note {
          background: #fff5f2;
          border-left: 4px solid #ff6b35;
          padding: 20px;
          border-radius: 8px;
          margin-top: 40px;
        }

        .important-note h3 {
          font-size: 18px;
          color: #ff6b35;
          margin-bottom: 12px;
          font-weight: 700;
        }

        .important-note p {
          font-size: 16px;
          color: #4a5568;
          line-height: 1.6;
          margin: 0;
        }

        @media (max-width: 768px) {
          .privacy-hero {
            padding: 60px 20px 40px;
          }

          .privacy-hero h1 {
            font-size: 32px;
          }

          .privacy-hero p {
            font-size: 16px;
          }

          .privacy-container {
            padding: 40px 20px;
          }

          .privacy-section {
            padding: 24px;
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 12px;
          }

          .section-header h2 {
            font-size: 20px;
          }

          .section-content p {
            font-size: 15px;
          }
        }
      `}</style>
<Header language={language}/>
      <div className="privacy-page">
        {/* Hero Section */}
        <div className="privacy-hero">
          <div className="privacy-hero-content">
            <h1>{t.title}</h1>
            <p>{t.subtitle}</p>
            <div className="last-updated">{t.lastUpdated}</div>
          </div>
        </div>

        {/* Privacy Content */}
        <div className="privacy-container">
          {t.sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <div key={index} className="privacy-section">
                <div className="section-header">
                  <div className="section-icon">
                    <IconComponent size={24} />
                  </div>
                  <h2>{section.title}</h2>
                </div>
                <div className="section-content">
                  {section.content.map((paragraph, pIndex) => (
                    <p key={pIndex}>{paragraph}</p>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Important Note */}
          <div className="important-note">
            <h3>
              {language === 'mr' ? '🔒 गोपनीयता आश्वासन' : '🔒 Privacy Assurance'}
            </h3>
            <p>
              {language === 'mr'
                ? 'आम्ही तुमच्या वैयक्तिक माहितीचे संरक्षण करण्यास वचनबद्ध आहोत. प्रश्न असल्यास आमच्याशी संपर्क साधा.'
                : 'We are committed to protecting your personal information. Contact us if you have any questions.'}
            </p>
          </div>
        </div>
      </div>
      <Footer language={language}/>
    </>
  );
};

export default PrivacyPolicy;